import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionService } from '../services/subscriptionService';
import { companyService } from '@features/companies/services/companyService';
import type { UpgradeDowngradeRequest, CardInfo } from '../types/subscription.types';
import { useReportError } from '@features/support/SupportErrorProvider';
import { extractErrorInfo } from '@features/support/errorInfo';

/**
 * Hook to get available plans with current subscription info
 * This uses the new endpoint that includes subscription status
 */
export function useAvailablePlans() {
  return useQuery({
    queryKey: ['subscription', 'available-plans'],
    queryFn: () => subscriptionService.getAvailablePlans(),
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useCurrentSubscription() {
  return useQuery({
    queryKey: ['subscription', 'current'],
    queryFn: () => subscriptionService.getCurrentSubscription(),
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useUpgradePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpgradeDowngradeRequest) => subscriptionService.upgradePlan(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription', 'current'] });
    },
  });
}

export function useDowngradePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpgradeDowngradeRequest) => subscriptionService.downgradePlan(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription', 'current'] });
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => subscriptionService.cancelSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription', 'current'] });
    },
  });
}

/**
 * Hook para verificar si la compañía seleccionada tiene una suscripción activa
 */
export function useSubscriptionVerification() {
  return useQuery({
    queryKey: ['subscription-verification'],
    queryFn: () => companyService.verifySubscription(),
    staleTime: 0,
    gcTime: 0,
    retry: 1,
  });
}

/**
 * Hook para crear una suscripción gratuita automáticamente
 */
export function useCreateFreeSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => companyService.createFreeSubscription(),
    onSuccess: () => {
      // Invalidar las queries relacionadas con suscripciones
      queryClient.invalidateQueries({ queryKey: ['subscription-verification'] });
      queryClient.invalidateQueries({ queryKey: ['subscription', 'current'] });
    },
  });
}

/**
 * Hook para cambiar plan con pago
 */
export function useChangePlanWithPayment() {
  const queryClient = useQueryClient();
  const reportError = useReportError();

  return useMutation({
    mutationFn: ({
      subscriptionId,
      newPlanId,
      cardInfo,
      amount,
      customerName,
      customerPhone,
      customerAddress,
    }: {
      subscriptionId: string;
      newPlanId: string;
      cardInfo: CardInfo;
      amount: number;
      customerName?: string;
      customerPhone?: string;
      customerAddress?: string;
    }) =>
      subscriptionService.changePlanWithPayment(
        subscriptionId,
        newPlanId,
        cardInfo,
        amount,
        customerName,
        customerPhone,
        customerAddress
      ),
    onSuccess: () => {
      // Invalidar todas las queries relacionadas con suscripciones
      queryClient.invalidateQueries({ queryKey: ['subscription-verification'] });
      queryClient.invalidateQueries({ queryKey: ['subscription', 'current'] });
      queryClient.invalidateQueries({ queryKey: ['subscription', 'available-plans'] });
    },
    onError: (error: any) => {
      reportError({ title: 'Error al cambiar de plan', screen: 'planes y suscripción', ...extractErrorInfo(error) });
    },
  });
}
